import React, { useState } from 'react';
import { User, Phone, Mail, MessageSquare, ChevronDown, Truck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { CollapsibleCard } from '../ui/CollapsibleCard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * Sidebar Right - Fixe avec toutes les interactions et contacts
 */
const SidebarRight = ({ 
  operation,
  ActionsComponent,
  VotreInterlocuteur,
  onSendMessage,
  children 
}) => {
  // Récupérer les infos de contact
  const operatorPhone = operation.assignedOperatorPhone || operation.phone;
  const operatorEmail = operation.assignedOperatorEmail || operation.email;
  
  // Déterminer si on a plusieurs contacts
  const hasMultipleContacts = operation.assignedOperatorName && operation.assignedFieldOpsName;
  
  // Fonction pour gérer le clic sur "Envoyer un message"
  const handleSendMessage = (recipient) => {
    // Passer le destinataire à la fonction parent
    if (onSendMessage) {
      onSendMessage(recipient);
    }
  };

  return (
    <div className="lg:sticky lg:top-4 space-y-4">
      {/* 1. Actions Component (Tarification + Actions) */}
      {ActionsComponent}

      {/* 2. Vos Interlocuteurs */}
      {(operation.assignedOperatorName || operation.assignedFieldOpsName) && (
        <CollapsibleCard
          id="op-interlocuteurs"
          title="Vos Interlocuteurs"
          icon={User}
          defaultOpen={true}
          className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50"
          headerClassName="text-purple-900"
          summaryText={[operation.assignedOperatorName, operation.assignedFieldOpsName, operation.partnerName].filter(Boolean).join(' · ')}
        >
          <div className="space-y-3">
            
            {/* Operator Card */}
            {operation.assignedOperatorName && (
              <div className="bg-white rounded-lg p-3 border border-purple-100">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar className="h-12 w-12 bg-gradient-to-br from-purple-500 to-pink-500">
                    <AvatarFallback className="text-white font-bold">
                      {operation.assignedOperatorName?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-gray-900">
                      {operation.assignedOperatorName}
                    </p>
                    <p className="text-xs text-purple-600">Opérateur assigné</p>
                  </div>
                </div>
                
                {/* Contact Actions */}
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start text-xs hover:bg-purple-50"
                    onClick={() => handleSendMessage({ 
                      type: 'operator',
                      id: operation.assignedOperatorId,
                      name: operation.assignedOperatorName 
                    })}
                  >
                    <MessageSquare className="w-3 h-3 mr-2" />
                    Envoyer un message
                  </Button>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 justify-start text-xs hover:bg-purple-50"
                      onClick={() => {
                        if (operatorPhone) {
                          window.location.href = `tel:${operatorPhone}`;
                        }
                      }}
                    >
                      <Phone className="w-3 h-3 mr-1" />
                      Appeler
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 justify-start text-xs hover:bg-purple-50"
                      onClick={() => {
                        if (operatorEmail) {
                          window.location.href = `mailto:${operatorEmail}`;
                        }
                      }}
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      Email
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Field Ops Card */}
            {operation.assignedFieldOpsName && (
              <div className="bg-white rounded-lg p-3 border border-cyan-100">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar className="h-12 w-12 bg-gradient-to-br from-cyan-500 to-blue-500">
                    <AvatarFallback className="text-white font-bold">
                      {operation.assignedFieldOpsName?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-gray-900">
                      {operation.assignedFieldOpsName}
                    </p>
                    <p className="text-xs text-cyan-600">Field Ops assigné</p>
                  </div>
                </div>
                
                {/* Contact Actions */}
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start text-xs hover:bg-cyan-50"
                    onClick={() => handleSendMessage({ 
                      type: 'field_ops',
                      id: operation.assignedFieldOpsId,
                      name: operation.assignedFieldOpsName 
                    })}
                  >
                    <MessageSquare className="w-3 h-3 mr-2" />
                    Envoyer un message
                  </Button>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 justify-start text-xs hover:bg-cyan-50"
                      onClick={() => {
                        const fieldOpsPhone = operation.assignedFieldOpsPhone || operatorPhone;
                        if (fieldOpsPhone) {
                          window.location.href = `tel:${fieldOpsPhone}`;
                        }
                      }}
                    >
                      <Phone className="w-3 h-3 mr-1" />
                      Appeler
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 justify-start text-xs hover:bg-cyan-50"
                      onClick={() => {
                        const fieldOpsEmail = operation.assignedFieldOpsEmail || operatorEmail;
                        if (fieldOpsEmail) {
                          window.location.href = `mailto:${fieldOpsEmail}`;
                        }
                      }}
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      Email
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Transport Partner Card */}
            {operation.partnerName && (
              <div className="bg-white rounded-lg p-3 border border-emerald-100" data-testid="transport-partner-card">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-teal-500">
                    <AvatarFallback className="text-white font-bold text-xs">
                      <Truck className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-gray-900">
                      {operation.partnerName}
                    </p>
                    <p className="text-xs text-emerald-600">Transporteur</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Info */}
            <div className="text-xs text-center text-purple-700 bg-purple-100 rounded-lg py-2 px-3">
              💡 Disponible pour répondre à vos questions
            </div>
          </div>
        </CollapsibleCard>
      )}

      {/* 3. Additional Content from children (Vos informations) */}
      {children}
    </div>
  );
};

export default SidebarRight;
